import type { FlowMetricMode } from './presets';
import { getSectorShortName } from './sectors';
import { SortDirection } from './types';

const TOTAL_TO_NET_SORT_KEYS: Record<string, string> = {
  sentimentScore: 'netPremiumShare',
  contracts: 'netContracts',
  premium: 'netPremium',
  unusualContracts: 'netUnusualContracts',
  unusualPremium: 'netUnusualPremium',
  sweepContracts: 'netSweepContracts',
  sweepPremium: 'netSweepPremium',
};

const NET_TO_TOTAL_SORT_KEYS: Record<string, string> = Object.entries(
  TOTAL_TO_NET_SORT_KEYS,
).reduce(
  (acc, [totalKey, netKey]) => ({
    ...acc,
    [netKey]: totalKey,
  }),
  {},
);

const LEGACY_TOTAL_SORT_KEYS: Record<string, string> = {
  putFlow: 'sentimentScore',
};

type SortableTickerAggregate = {
  ticker?: string | null;
  contracts?: number | null;
  netPremium?: number | null;
};

export const getTickerAggregateSortKeyForMetricMode = (
  sortKey: string,
  metricMode: FlowMetricMode,
) => {
  if (metricMode === 'net') {
    const totalSortKey = LEGACY_TOTAL_SORT_KEYS[sortKey] || sortKey;
    return TOTAL_TO_NET_SORT_KEYS[totalSortKey] || totalSortKey;
  }

  return LEGACY_TOTAL_SORT_KEYS[sortKey] || NET_TO_TOTAL_SORT_KEYS[sortKey] || sortKey;
};

export const getNextTickerAggregateSortState = ({
  currentSortKey,
  currentSortDirection,
  selectedSortKey,
  defaultSortKey,
}: {
  currentSortKey: string;
  currentSortDirection: SortDirection;
  selectedSortKey: string;
  defaultSortKey: string;
}) => {
  if (currentSortKey !== selectedSortKey || currentSortDirection === SortDirection.None) {
    return {
      sortKey: selectedSortKey,
      sortDirection: SortDirection.Desc,
    };
  }

  if (currentSortDirection === SortDirection.Desc) {
    return {
      sortKey: selectedSortKey,
      sortDirection: SortDirection.Asc,
    };
  }

  return {
    sortKey: defaultSortKey,
    sortDirection: SortDirection.None,
  };
};

const getSortValue = <T extends SortableTickerAggregate>(
  ticker: T,
  sortKey: string,
): string | number | null => {
  const rawValue = (ticker as Record<string, unknown>)[sortKey];

  // Sort the sector column by the short name that is actually displayed in the
  // table (e.g. "Information Technology" -> "Tech") so the order matches what the
  // user sees. getSectorShortName maps empty/unknown sectors to "-".
  if (sortKey === 'sector') {
    return getSectorShortName(String(rawValue ?? '').trim()).toLocaleLowerCase();
  }

  if (rawValue == null) {
    return null;
  }

  if (typeof rawValue === 'number') {
    return Number.isFinite(rawValue) ? rawValue : null;
  }

  const numericValue = Number(rawValue);
  if (typeof rawValue !== 'boolean' && rawValue !== '' && Number.isFinite(numericValue)) {
    return numericValue;
  }

  const textValue = String(rawValue).trim();
  return textValue ? textValue.toLocaleLowerCase() : null;
};

const compareValues = (first: string | number, last: string | number) => {
  if (typeof first === 'string' || typeof last === 'string') {
    return String(first).localeCompare(String(last));
  }

  return first - last;
};

const compareNullableValues = (
  firstValue: string | number | null,
  lastValue: string | number | null,
  direction: number,
) => {
  if (firstValue == null && lastValue != null) {
    return 1;
  }

  if (firstValue != null && lastValue == null) {
    return -1;
  }

  const result = firstValue == null || lastValue == null ? 0 : compareValues(firstValue, lastValue);

  return result * direction;
};

export const sortTickerAggregates = <T extends SortableTickerAggregate>({
  tickers,
  sortKey,
  sortDirection,
  metricMode,
  defaultSortKey,
}: {
  tickers: T[];
  sortKey: string;
  sortDirection: SortDirection;
  metricMode: FlowMetricMode;
  defaultSortKey: string;
}): T[] => {
  const hasActiveSort = Boolean(sortKey) && sortDirection !== SortDirection.None;
  const activeSortKey = hasActiveSort
    ? getTickerAggregateSortKeyForMetricMode(sortKey, metricMode)
    : metricMode === 'net'
    ? 'netPremium'
    : defaultSortKey;
  // Net mode's default (no explicit column sort) ranks by ABSOLUTE net premium
  // descending — the biggest movers in either direction surface first. An explicit
  // Net Premium column click still sorts by signed value, so "most bullish" is one
  // click away while "biggest movers" stays the default-view discovery surface.
  const isDefaultNetSort = !hasActiveSort && metricMode === 'net' && activeSortKey === 'netPremium';
  const direction = sortDirection === SortDirection.Asc ? 1 : -1;

  return tickers
    .map((ticker, index) => ({ ticker, index }))
    .sort((a, b) => {
      if (isDefaultNetSort) {
        const firstNetPremium = getSortValue(a.ticker, activeSortKey);
        const lastNetPremium = getSortValue(b.ticker, activeSortKey);
        const nullResult = compareNullableValues(firstNetPremium, lastNetPremium, -1);

        if (firstNetPremium == null || lastNetPremium == null) {
          if (nullResult !== 0) {
            return nullResult;
          }
        }

        const firstAbs = Math.abs(Number(firstNetPremium));
        const lastAbs = Math.abs(Number(lastNetPremium));
        const absResult = lastAbs - firstAbs;

        if (absResult !== 0) {
          return absResult;
        }
      } else {
        const firstValue = getSortValue(a.ticker, activeSortKey);
        const lastValue = getSortValue(b.ticker, activeSortKey);
        const result = compareNullableValues(firstValue, lastValue, direction);

        if (result !== 0) {
          return result;
        }
      }

      const contractsResult = (b.ticker.contracts || 0) - (a.ticker.contracts || 0);
      if (contractsResult !== 0) {
        return contractsResult;
      }

      const tickerResult = String(a.ticker.ticker || '').localeCompare(
        String(b.ticker.ticker || ''),
      );
      if (tickerResult !== 0) {
        return tickerResult;
      }

      return a.index - b.index;
    })
    .map(({ ticker }) => ticker);
};
